import * as React from "react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import FieldTypesContext from "views/components/contexts/FieldTypesContext";
import FieldTypeMapping from "views/logic/fieldtypes/FieldTypeMapping";
import FieldType from "views/logic/fieldtypes/FieldType";
import { defaultCompare } from "logic/DefaultCompare";
import { useIsInteractiveMode } from "views/components/contexts/InteractiveContext";
import WatchlistStatusContext from "watchlist/contexts/WatchlistStatusContext";
import useWatchlistMutations from "watchlist/hooks/useWatchlistMutations";
import defaultWatchlists from "security-content/mappings/watchlists";
import useLicenseCheck from "license/useLicenseCheck";
import usePermissions from "hooks/usePermissions";
import WatchlistIndicator from "./WatchlistIndicator";
const getWatchlistByType = (watchlists, type) => {
  const watchlist = Object.values(watchlists).find(({ fieldType }) => fieldType === type);
  if (!watchlist) {
    throw Error(`Could not find watchlist with field type ${type}`);
  }
  return watchlist.title;
};
const removeFieldFromWatchlist = (fieldName, fieldValue, watchlistTitle, watchlistFieldType, matchedEntry, matchedViaRange, removeFromWatchlist) => {
  const confirmMessage = matchedViaRange ? i18n.t("watchlist_augmentation.confirm_remove_range", {
    ns: "enterprise_search",
    matchedEntry,
    watchlistTitle
  }) : i18n.t("watchlist_augmentation.confirm_remove_value", {
    ns: "enterprise_search",
    fieldName,
    fieldValue,
    watchlistTitle
  });
  const valueToRemove = matchedViaRange ? matchedEntry : fieldValue;
  if (window.confirm(confirmMessage)) {
    removeFromWatchlist({ watchlistFieldType, value: valueToRemove });
  }
};
const getEnrichedFields = (watchlists, message, queryFields, fieldsWatchlistStatus) => {
  const fields = Object.entries(fieldsWatchlistStatus).reduce((enrichedFields, [watchlistFieldType, matches]) => {
    const watchlistTitle = getWatchlistByType(watchlists, watchlistFieldType);
    const enrichedWatchlistFields = (matches != null ? matches : []).map(
      ({ fieldName, watchlistName, matchedEntry, matchedViaRange }) => {
        const fieldValue = message.fields[fieldName];
        const { type: fieldType } = queryFields.find(
          (t) => t.name === fieldName,
          void 0,
          FieldTypeMapping.create(fieldName, FieldType.Unknown)
        );
        return {
          fieldType,
          fieldName,
          fieldValue,
          watchlistTitle,
          watchlistFieldType,
          watchlistName,
          matchedEntry,
          matchedViaRange
        };
      }
    );
    return [...enrichedFields, ...enrichedWatchlistFields];
  }, []);
  return fields.sort(({ fieldName: fieldName1 }, { fieldName: fieldName2 }) => defaultCompare(fieldName1, fieldName2));
};
const WatchlistAugmentation = ({ message, watchlists = defaultWatchlists }) => {
  const { status: watchlistStatus, isLoading } = useContext(WatchlistStatusContext) || {};
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  const fieldTypes = useContext(FieldTypesContext);
  const { isPermitted } = usePermissions();
  const interactive = useIsInteractiveMode();
  const { removeFromWatchlist } = useWatchlistMutations();
  const { t } = useTranslation("enterprise_search");
  const allowRemoval = interactive && isPermitted("watchlist:delete");
  if (!isValidSecurityLicense || isLoading || !watchlistStatus || Object.values(watchlistStatus).length === 0) {
    return null;
  }
  const queryFields = fieldTypes.currentQuery;
  const enrichedFields = getEnrichedFields(watchlists, message, queryFields, watchlistStatus);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("dt", null, t("watchlist_augmentation.found_on_watchlist")), enrichedFields.map(
    ({
      fieldName,
      fieldValue,
      fieldType,
      watchlistTitle,
      watchlistFieldType,
      watchlistName,
      matchedEntry,
      matchedViaRange
    }) => /* @__PURE__ */ React.createElement(
      WatchlistIndicator,
      {
        key: `${watchlistTitle}-${fieldName}-${matchedEntry}`,
        title: watchlistTitle,
        watchlistName,
        matchedEntry,
        matchedViaRange,
        fieldName,
        fieldValue,
        message,
        realFieldType: fieldType,
        allowRemoval,
        onDelete: () => removeFieldFromWatchlist(
          fieldName,
          fieldValue,
          watchlistTitle,
          watchlistFieldType,
          matchedEntry,
          matchedViaRange,
          removeFromWatchlist
        )
      }
    )
  ));
};
export default WatchlistAugmentation;
